import styled from 'styled-components';
import { DiagramEngine } from '@projectstorm/react-diagrams';
import { EventMapDiagramModel } from '../EventMapDiagramModel';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { TinyTextInput } from '../InputControls/TinyTextInput';
import {
  AllNodeMap,
  mapScenarios,
  NodeKeys,
  MapScenarioType,
} from '@terragotech/gen5-datamapping-lib';
import { useHotkeys } from 'react-hotkeys-hook';
import { getCategoryColor } from 'utils/Utils';
import { colors } from 'utils/colors';

export const DROP_LAYER_DATA_TAG = 'EVENT_MAPPER';
const Wrapper = styled.div`
  flex: 1;
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
`;
const ContextBackground = styled.div`
  position: absolute;
  background-color: ${colors.black3};
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;
const ContextMenu = styled.div<{ xPos: number; yPos: number }>`
  background-color: ${colors.white};
  color: ${colors.black};
  position: fixed;
  left: ${(props) => props.xPos + 'px'};
  top: ${(props) => props.yPos + 'px'};
  width: 275px;
  max-height: 400px;
  height: 305px;
  display: flex;
  flex-direction: column;
  border-radius: 15px;
  box-shadow: 0px 4px 15px 0px ${colors.black25};
  margin-bottom: 10px;
`;
const TopSection = styled.div`
  font-size: 12px;
  color: ${colors.white};
  display: flex;
  flex-direction: column;
`;
const Title = styled.div`
  font-size: 16px;
  color: ${colors.black};
  padding: 10px;
  padding-bottom: 0px;
`;
const SearchSection = styled.div`
  padding: 10px;
  display: flex;
`;
const ResultSection = styled.div`
  overflow-y: auto;
  margin-bottom: 10px;

  /* width */
  ::-webkit-scrollbar {
    width: 14px;
  }

  /* Track */
  ::-webkit-scrollbar-track {
    box-shadow: inset 0 0 5px white;
    border-radius: 10px;
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: ${colors.lightGray10};
    border-radius: 10px;
  }
`;
const ResultCategory = styled.div`
  top: 0px;
  position: -webkit-sticky;
  position: sticky;
  font-size: 15px;
  font-weight: 500;
  line-height: 15px;
  letter-spacing: 0em;
  text-align: left;
  color: ${colors.black};
  cursor: pointer;
  margin-left: 13px;
  background: ${colors.paleWhite10};
  column-gap: 5px;
  display: flex;
  height: 20px;
`;
const ResultEntry = styled.div<{ isSelected?: boolean }>`
  font-size: 12px;
  color: ${colors.white};
  cursor: pointer;
  margin-left: 34px;
  ${(props) =>
    props.isSelected
      ? `background: linear-gradient(225deg, ${colors.black3}, ${colors.black2});`
      : ''}
`;

const ColorPallet = styled.div<{ category?: string }>`
  width: 14px;
  height: 14px;
  ${(props) => (props.category ? `background: ${getCategoryColor(props.category)};` : '')}
`;

const style = {
  containerStyle: {
    background: colors.white,
  },
  resultContainer: {
    background: colors.white,
    color: colors.black,
  },
  resultLabel: {
    color: colors.black,
  },
  search: {
    height: 30,
    padding: '4px, 237px, 5px, 7px',
    borderRadius: 5,
    background: colors.white10,
    border: `1px solid ${colors.grayBorder} `,
  },
};

export interface DroppableNode {
  type: string;
}

export interface DropLayerProps {
  diagramEngine: DiagramEngine;
  mapScenario?: keyof MapScenarioType;
  children: React.ReactNode;
}
const MenuManager: React.FunctionComponent<DropLayerProps> = (props) => {
  const { diagramEngine, mapScenario } = props;
  const [xPos, setXPos] = useState(0);
  const [yPos, setYPos] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchText, setSearchText] = useState<string | null>('');
  const [selectedResult, setSelectedResult] = useState<string | null>(null);

  const handleContextMenu = useCallback<React.MouseEventHandler<HTMLDivElement>>(
    (e) => {
      e.preventDefault();
      //The following is the best I could come up with to determine if the user right clicked on a link
      //This prevents the new node menu from popping up when a link is deleted
      //@ts-ignore
      if (!(e.target?.nodeName === 'path' && e.target?.selected)) {
        setXPos(e.clientX);
        setYPos(e.clientY);
        setMenuOpen((cur) => !cur);
      }
    },
    [setXPos, setYPos]
  );
  const handleContextClose = useCallback(() => {
    setSelectedResult(null);
    setSearchText(null);
    setMenuOpen(false);
  }, [setMenuOpen]);

  //Sort everything into categories and filter based on the search bar
  const results = useMemo(() => {
    const categorizedOutput: {
      [index: string]: { [key: string]: { value: string; label: string } };
    } = {}; //Object.values(AllNodeMap).reduce(

    Object.keys(AllNodeMap)
      .filter((key) => {
        return mapScenario
          ? mapScenarios[mapScenario].availableNodes.includes(key as NodeKeys)
          : true;
      })
      .filter(
        (key) =>
          !searchText ||
          (AllNodeMap as any)[key].nodeDefinition.defaultTitle
            .toLowerCase()
            .includes(searchText.toLowerCase())
      )
      .forEach((key) => {
        const { primaryCategory, NODE_TYPE, defaultTitle } = (AllNodeMap as any)[
          key
        ].nodeDefinition;
        categorizedOutput[primaryCategory] = {
          ...categorizedOutput[primaryCategory],
          [key]: { value: NODE_TYPE, label: defaultTitle },
        };
      });
    return categorizedOutput;
  }, [mapScenario, searchText]);

  const categories = Object.keys(results);
  //If the currently selected item is no longer visible, select first item
  useEffect(() => {
    const category = categories.find(
      (key) =>
        Object.keys(results[key]).findIndex(
          (itemKey) => results[key][itemKey].value === selectedResult
        ) >= 0
    );
    if (!category) {
      const firstCategory = Object.keys(results)[0];
      const firstItem = firstCategory
        ? results[firstCategory][Object.keys(results[firstCategory])[0]].value
        : null;
      setSelectedResult(firstItem as string | null);
    }
  }, [categories, results, selectedResult]);
  //Determine the category and index of the currently selected item
  const [category, itemIndex] = useMemo(() => {
    if (menuOpen && results && categories.length > 0) {
      const category =
        categories.find(
          (key) =>
            Object.keys(results[key]).findIndex(
              (itemKey) => results[key][itemKey].value === selectedResult
            ) >= 0
        ) || Object.keys(results)[0];
      const itemIndex =
        results[category] &&
        Object.keys(results[category]).findIndex((itemKey) => itemKey === selectedResult);
      return [category, itemIndex];
    }
    return ['', undefined];
  }, [categories, menuOpen, results, selectedResult]);
  //handle up arrow
  useHotkeys(
    'up',
    () => {
      //try to decrease the index
      if (itemIndex !== undefined && itemIndex > 0) {
        const newKey = Object.keys(results[category])[itemIndex - 1];
        setSelectedResult(newKey);
      } else {
        //if we would go less than 0 try to decrease the category and move to the last item
        const catIndex = categories.findIndex((key) => key === category);
        if (catIndex > 0) {
          const newCat = Object.keys(results)[catIndex - 1];
          const itemsInCategory = Object.keys(results[newCat]);
          const newKey = itemsInCategory[itemsInCategory.length - 1];
          setSelectedResult(newKey);
        }
      }
    },
    { enableOnTags: ['INPUT'] }
  );
  //handle down arrow
  useHotkeys(
    'down',
    () => {
      //TODO: add in handler for when nothing is selected.
      // check if the index is the last in the category
      const itemsInCategory = Object.keys(results[category || '']);
      if (itemIndex !== undefined && itemIndex < itemsInCategory.length - 1) {
        // if not, then increase
        const newKey = Object.keys(results[category])[itemIndex + 1];
        setSelectedResult(newKey);
      } else {
        // if item was last, check for a following category
        const catIndex = categories.findIndex((key) => key === category);
        if (catIndex < categories.length - 1) {
          // if category exists, move to its first item
          const newCat = Object.keys(results)[catIndex + 1];
          const itemsInCategory = Object.keys(results[newCat]);
          const newKey = itemsInCategory[0];
          setSelectedResult(newKey);
        }
      }
    },
    { enableOnTags: ['INPUT'] }
  );
  //handle enter selection
  useHotkeys(
    'enter',
    () => {
      menuOpen && selectedResult && handleChosenNode(selectedResult);
    },
    { enableOnTags: ['INPUT'] }
  );
  useHotkeys(
    'escape',
    () => {
      handleContextClose();
    },
    { enableOnTags: ['INPUT'] }
  );

  // handle the selection of a new node
  const handleChosenNode = useCallback(
    (node: string) => {
      const factories = diagramEngine.getNodeFactories();
      const factory = factories.getFactory(node);
      if (factory) {
        // grab a unique name here
        const temp = (diagramEngine.getModel() as EventMapDiagramModel).getUniqueName();
        const newModel = factory.generateModel({
          initialConfig: { id: temp, newlyCreated: true },
        });
        // set the position of that node based on the mouse
        const point = diagramEngine.getRelativeMousePoint({ clientX: xPos, clientY: yPos });
        newModel.setPosition(point);
        diagramEngine.getModel().addNode(newModel);
      }
      handleContextClose();
    },
    [diagramEngine, handleContextClose, xPos, yPos]
  );
  return (
    <Wrapper onContextMenu={handleContextMenu}>
      {props.children}
      {menuOpen && (
        <>
          <ContextBackground onClick={handleContextClose} />

          <ContextMenu xPos={xPos} yPos={yPos}>
            <TopSection>
              <Title>Add a Node</Title>

              <SearchSection>
                <TinyTextInput
                  style={style.search}
                  onChange={(value) => setSearchText(value || null)}
                  value={searchText}
                  placeholder={'Search'}
                  autoFocus={true}
                />
              </SearchSection>
            </TopSection>

            <ResultSection>
              <div style={style.resultContainer}>
                {Object.keys(results).map((category) => {
                  return (
                    <Fragment key={category}>
                      <ResultCategory>
                        <ColorPallet category={category} />
                        {category}
                      </ResultCategory>
                      {Object.keys(results[category]).map((nodeKey) => {
                        const node = results[category][nodeKey];
                        return (
                          <ResultEntry
                            isSelected={selectedResult === nodeKey}
                            onClick={() => handleChosenNode(node.value)}
                            key={node.value}
                            onMouseEnter={() => setSelectedResult(nodeKey)}
                            style={style.resultLabel}
                          >
                            {node.label}
                          </ResultEntry>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </div>
            </ResultSection>
          </ContextMenu>
        </>
      )}
    </Wrapper>
  );
};
export default MenuManager;
